import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { createJoinPaymentSessionWithRetry, createStakePaymentSessionWithRetry } from '../../api/paymentService';
import { PAYMENT_SESSION_EXPIRY_MS } from '../../constants/payment';
import { joinTournament } from '../../api/tournamentService';
import type { Payment, StakePaymentSession, PaymentSessionUpdatedPayload } from '../../types/payment';
import { useUser } from '../../api/userService';
import { initializeSocket, socket } from '../../api/socketService';
import {
  PaymentSessionSchema,
  getPaymentSession,
  setPaymentSession,
  clearPaymentSession,
  createSessionSchema,
} from './paymentSessionStorage';
import {
  trackPaymentInitiated,
  trackTournamentJoined,
  trackJoinFailed,
} from '../../utils/analytics/paymentEvents';
import { sanitizePaymentError, SanitizedError } from '../../utils/payment/errorSanitizer';

export type TournamentJoinPaymentState =
  | 'idle'
  | 'loading'
  | 'awaiting_payment'
  | 'polling'
  | 'success'
  | 'failed'
  | 'expired'
  | 'error';

export interface UseTournamentJoinPaymentOptions {
  tournamentId: string;
  /** Callback when payment is completed and tournament is joined */
  onPaymentComplete?: () => void;
}

export interface UseTournamentJoinPaymentResult {
  /** Current state of the payment flow */
  state: TournamentJoinPaymentState;
  /** Payment ID if session was created */
  paymentId: string | null;
  /** Full payment object if available */
  payment: Payment | null;
  /** Error if any occurred (sanitized for UI display) */
  error: SanitizedError | null;
  /** Initiate the Pay & Join flow */
  handlePayAndJoin: () => Promise<void>;
  /** Complete payment and join tournament (called when payment is confirmed) */
  completePayment: () => Promise<void>;
  /** Reset to idle state */
  reset: () => void;
  /** Clear persisted session from localStorage */
  clearSession: () => void;
  /** Whether a valid (unexpired) session exists for recovery */
  hasExistingSession: boolean;
  /** Session data for recovery UI (null if no valid session) */
  recoverySession: PaymentSessionSchema | null;
  /** Resume an existing session (restore awaiting_payment state) */
  resumeSession: () => void;
  /** Dismiss existing session and allow fresh start */
  dismissSession: () => void;
  /** Unified payments: stake session with real-time status from socket */
  stakeSession: StakePaymentSession | null;
}

// Re-export PaymentSessionSchema for consumers
export type { PaymentSessionSchema } from './paymentSessionStorage';

/**
 * Hook to manage the tournament join payment flow state machine.
 * Handles session creation, state transitions, recovery, and cleanup.
 * 
 * PRD Reference: §6.2 Lifecycle & Recovery
 */
export function useTournamentJoinPayment(
  options: UseTournamentJoinPaymentOptions
): UseTournamentJoinPaymentResult {
  const { tournamentId, onPaymentComplete } = options;
  
  // Task 38.1: Correctly extract userId from useUser().userDetails.id
  // useUser() returns { userDetails: { id: number }, ... }, NOT { userId }
  const { userDetails } = useUser() || {};
  const userId = userDetails?.id != null ? String(userDetails.id) : undefined;

  const [state, setState] = useState<TournamentJoinPaymentState>('idle');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<SanitizedError | null>(null);
  // Track recovery session separately (not auto-restored)
  const [recoverySession, setRecoverySession] = useState<PaymentSessionSchema | null>(null);
  // Unified payments: stake payment session (source of truth)
  const [stakeSession, setStakeSession] = useState<StakePaymentSession | null>(null);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Track if a request is in flight to prevent duplicate calls
  const isInFlightRef = useRef(false);
  // Track if auto-complete has been triggered to prevent duplicate calls
  const autoCompleteTriggeredRef = useRef(false);

  // Get JWT token for socket connection
  const { jwt: token } = useUser() || {};

  useEffect(() => {
    isMountedRef.current = true;
    autoCompleteTriggeredRef.current = false;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Check for existing session on mount (for recovery UI, not auto-restore)
  useEffect(() => {
    if (!userId) {
      return;
    }

    const storedSession = getPaymentSession(userId, tournamentId);
    if (storedSession && state === 'idle') {
      setRecoverySession(storedSession);
    }
  }, [tournamentId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed: whether a valid session exists for recovery
  const hasExistingSession = useMemo(() => {
    return recoverySession !== null;
  }, [recoverySession]);

  // Unified payments: Socket listener for paymentSessionUpdated (source of truth)
  useEffect(() => {
    if (!token || !tournamentId || state !== 'awaiting_payment') return;

    const connectSocket = async () => {
      try {
        await initializeSocket(token, undefined, () => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[useTournamentJoinPayment] Socket connected for payment session updates');
          }
        });

        if (socket) {
          socket.on('paymentSessionUpdated', (data: PaymentSessionUpdatedPayload) => {
            const ps = data.paymentSession;
            // Check tournament match
            if (String(ps.tournament) === String(tournamentId) && isMountedRef.current) {
              // Map snake_case from socket to camelCase, including the
              // optional chain context (stake_currency / network_info)
              // shipped by current backends.
              const incoming: StakePaymentSession = {
                id: ps.id,
                status: ps.status,
                sessionType: ps.session_type,
                expectedAmount: ps.expected_amount,
                paidAmount: ps.paid_amount,
                paymentDeadline: ps.payment_deadline,
                paymentRequired: true,
                tournament: ps.tournament,
                stakeCurrency: ps.stake_currency
                  ? {
                      shortName: ps.stake_currency.short_name,
                      chain: ps.stake_currency.chain,
                      precision: ps.stake_currency.precision,
                    }
                  : undefined,
                networkInfo: ps.network_info
                  ? {
                      chain: ps.network_info.chain,
                      network: ps.network_info.network,
                      commitmentLevel: ps.network_info.commitment_level ?? null,
                    }
                  : undefined,
              };
              // Preserve hydrated chain context if the socket payload omits
              // it (legacy backends), so SOL sessions don't silently flip
              // to POL fallbacks through a partial overwrite.
              setStakeSession(prev => prev
                ? {
                    ...prev,
                    ...incoming,
                    stakeCurrency: incoming.stakeCurrency ?? prev.stakeCurrency,
                    networkInfo: incoming.networkInfo ?? prev.networkInfo,
                  }
                : incoming);

              // Log status change for debugging
              if (process.env.NODE_ENV !== 'production') {
                console.log('[useTournamentJoinPayment] Payment session updated:', incoming.status);
              }
            }
          });
        }
      } catch (err) {
        console.error('[useTournamentJoinPayment] Socket setup failed', err);
      }
    };

    connectSocket();

    return () => {
      if (socket) {
        socket.off('paymentSessionUpdated');
      }
    };
  }, [token, tournamentId, state]);

  // Unified payments: Auto-complete when stake session is confirmed
  useEffect(() => {
    if (!stakeSession || autoCompleteTriggeredRef.current) return;
    
    const isConfirmed = stakeSession.status === 'CONFIRMED_EXACT' || stakeSession.status === 'OVERPAID';
    
    if (isConfirmed && state === 'awaiting_payment' && paymentId) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[useTournamentJoinPayment] Payment confirmed, auto-completing...');
      }
      autoCompleteTriggeredRef.current = true;
      // completePayment will be called - but we need to define it first
      // So we trigger it via setState and the effect below
      setState('polling'); // Intermediate state before completion
    }
    
    // Handle timeout
    if (stakeSession.status === 'TIMEOUT' && state === 'awaiting_payment') {
      setState('expired');
    }
  }, [stakeSession, state, paymentId]);

  /**
   * Resume an existing session (restore awaiting_payment state)
   * PRD Reference: §6.2 Recovery Flow
   */
  const resumeSession = useCallback(() => {
    if (!recoverySession) {
      return;
    }
    setPaymentId(recoverySession.paymentId);
    setState('awaiting_payment');
  }, [recoverySession]);

  /**
   * Dismiss existing session and allow fresh start
   * PRD Reference: §6.2 Recovery Flow
   */
  const dismissSession = useCallback(() => {
    if (userId) {
      clearPaymentSession(userId, tournamentId);
    }
    setRecoverySession(null);
  }, [tournamentId, userId]);

  const handlePayAndJoin = useCallback(async () => {
    // Prevent multiple concurrent calls
    if (isInFlightRef.current || state !== 'idle') {
      return;
    }

    // Duplicate session guard: check if valid session already exists
    // PRD Reference: §6.2 Duplicate Guard
    if (userId) {
      const existingSession = getPaymentSession(userId, tournamentId);
      if (existingSession) {
        console.warn(
          `[useTournamentJoinPayment] Blocked: valid session exists for tournament ${tournamentId}. ` +
          `Use resumeSession() or dismissSession() first.`
        );
        return;
      }
    }

    // If we do not have userId yet, we still allow the flow but skip persistence
    if (!userId) {
      console.warn(
        '[useTournamentJoinPayment] Missing userId – proceeding without localStorage persistence.'
      );
    }

    isInFlightRef.current = true;
    setState('loading');
    setError(null);

    try {
      const response = await createJoinPaymentSessionWithRetry(tournamentId);

      if (isMountedRef.current) {
        setPaymentId(response.payment.id);
        setPayment(response.payment);
        setState('awaiting_payment');
        
        // Persist session to localStorage with extended v2/v3 schema (Task 37)
        // PRD Reference: §6.1 Target Schema
        if (userId) {
          const session = createSessionSchema(
            tournamentId,
            response.payment.id,
            response.payment.expiresAt || Date.now() + PAYMENT_SESSION_EXPIRY_MS,
            response.payment.amount,
            response.payment.currency
          );
          setPaymentSession(userId, tournamentId, session, { force: true });
          setRecoverySession(null); // Clear recovery state since we have a new session
        }

        // Track analytics event (non-blocking)
        // PRD Reference: §7 Analytics Model
        try {
          trackPaymentInitiated({
            paymentId: response.payment.id,
            tournamentId,
            amount: response.payment.amount,
            currency: response.payment.currency,
            paymentType: 'TOURNAMENT_JOIN_ENTRY',
          });
        } catch (analyticsError) {
          console.error('[useTournamentJoinPayment] Analytics error:', analyticsError);
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorWithCode = err as Error & { code?: string };
        const sanitized = sanitizePaymentError(err as Error);
        
        // Log full details for debugging (Task 33.3)
        console.error('[PaymentError]', {
          code: errorWithCode.code || 'UNKNOWN',
          details: sanitized.details,
        });
        
        setError(sanitized);
        setState('error');
      }
    } finally {
      isInFlightRef.current = false;
    }
  }, [tournamentId, state]);

  const reset = useCallback(() => {
    setState('idle');
    setPaymentId(null);
    setPayment(null);
    setError(null);
    isInFlightRef.current = false;
  }, []);

  /**
   * Complete payment by calling joinTournament with the paymentId.
   * Called when payment status becomes CONFIRMED (legacy) or CONFIRMED_EXACT/OVERPAID (unified payments).
   */
  const completePayment = useCallback(async () => {
    // Accept both awaiting_payment (manual call) and polling (auto-trigger from socket)
    if (!paymentId || (state !== 'awaiting_payment' && state !== 'polling')) {
      return;
    }

    setState('loading');

    // Get payment details for analytics
    const currentSession = userId ? getPaymentSession(userId, tournamentId) : null;
    const amount = payment?.amount || currentSession?.amount || '';
    const currency = payment?.currency || currentSession?.currency || '';

    try {
      await joinTournament(tournamentId, undefined, paymentId);

      if (isMountedRef.current) {
        setState('success');
        if (userId) {
          clearPaymentSession(userId, tournamentId);
        }

        // Track successful join (non-blocking)
        // PRD Reference: §7 Analytics Model
        try {
          trackTournamentJoined({
            tournamentId,
            paymentId,
            amount,
            currency,
            status: 200,
            alreadyJoined: false,
          });
        } catch (analyticsError) {
          console.error('[useTournamentJoinPayment] Analytics error:', analyticsError);
        }

        onPaymentComplete?.();
      }
    } catch (err) {
      // Treat 409 ALREADY_JOINED as success
      const axiosError = err as { response?: { status?: number; data?: { code?: string; message?: string } } };
      if (axiosError.response?.status === 409 && axiosError.response?.data?.code === 'ALREADY_JOINED') {
        if (isMountedRef.current) {
          setState('success');
          if (userId) {
            clearPaymentSession(userId, tournamentId);
          }

          // Track 409 as successful join (non-blocking)
          // PRD Reference: §7 Analytics Model
          try {
            trackTournamentJoined({
              tournamentId,
              paymentId,
              amount,
              currency,
              status: 409,
              alreadyJoined: true,
            });
          } catch (analyticsError) {
            console.error('[useTournamentJoinPayment] Analytics error:', analyticsError);
          }

          onPaymentComplete?.();
        }
        return;
      }

      if (isMountedRef.current) {
        const originalErrorMessage = axiosError.response?.data?.message || (err as Error).message;
        const errorWithCode = err as Error & { code?: string };
        const sanitized = sanitizePaymentError(err as Error);
        
        // Log full details for debugging (Task 33.3)
        console.error('[PaymentError]', {
          code: errorWithCode.code || axiosError.response?.data?.code || 'UNKNOWN',
          details: sanitized.details,
        });

        // Track join failure (non-blocking)
        // PRD Reference: §7 Analytics Model
        try {
          trackJoinFailed({
            tournamentId,
            paymentId,
            errorCode: axiosError.response?.data?.code,
            errorMessage: originalErrorMessage, // Keep original for analytics
          });
        } catch (analyticsError) {
          console.error('[useTournamentJoinPayment] Analytics error:', analyticsError);
        }

        setError(sanitized);
        setState('error');
      }
    }
  }, [paymentId, payment, state, tournamentId, onPaymentComplete]);

  const clearSession = useCallback(() => {
    if (userId) {
      clearPaymentSession(userId, tournamentId);
    }
    setRecoverySession(null);
  }, [tournamentId, userId]);

  // Unified payments: Auto-call completePayment when state transitions to 'polling'
  // This is triggered by the socket listener when payment is CONFIRMED_EXACT/OVERPAID
  useEffect(() => {
    if (state === 'polling' && paymentId) {
      completePayment();
    }
  }, [state, paymentId, completePayment]);

  return {
    state,
    paymentId,
    payment,
    error,
    handlePayAndJoin,
    completePayment,
    reset,
    clearSession,
    hasExistingSession,
    recoverySession,
    resumeSession,
    dismissSession,
    // Unified payments: stake session with real-time status
    stakeSession,
  };
}
